/**
 * Remote persistence client — the Azure Table adapter (via the API) behind the
 * repository seam. Best-effort: every call fails soft so the standalone POC
 * keeps working against its local cache when the API/storage is absent (the
 * endpoints return 503 when storage is unconfigured).
 */
import { migrateIncident, type Incident, type AuditEvent } from '@duty/shared';

/** Fetch the server snapshot of an incident, or null if unavailable/not found. */
export async function remoteGetIncident(id: string, signal?: AbortSignal): Promise<Incident | null> {
  try {
    const res = await fetch(`/api/incident/${encodeURIComponent(id)}`, {
      signal: signal ?? AbortSignal.timeout(6000),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<Incident> & { schemaVersion?: number };
    return data?.id && data.itasc ? migrateIncident(data) : null;
  } catch {
    return null;
  }
}

/** Write-through the incident snapshot. Returns true when the server accepted it. */
export async function remotePutIncident(incident: Incident): Promise<boolean> {
  try {
    const res = await fetch(`/api/incident/${encodeURIComponent(incident.id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(incident),
      signal: AbortSignal.timeout(6000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Append audit events for the incident. Returns the number the server wrote. */
export async function remoteAppendAudit(incidentId: string, events: readonly AuditEvent[]): Promise<number> {
  if (events.length === 0) return 0;
  try {
    const res = await fetch(`/api/incident/${encodeURIComponent(incidentId)}/audit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events }),
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return 0;
    const data = (await res.json()) as { written?: number };
    return data.written ?? 0;
  } catch {
    return 0;
  }
}
